import React, { useState, useEffect } from 'react';
import { Activity, HeartPulse, CheckCircle, ArrowRight, Smartphone, Sparkles, ShieldAlert } from 'lucide-react';
import { connectProvider } from '../utils/capacitorUtils';

const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');

const PARQ_QUESTIONS = [
  { id: 'q1_heart', text: '1. ¿Alguna vez un médico te ha dicho que tienes una afección cardíaca o hipertensión arterial?' },
  { id: 'q2_chest_pain', text: '2. ¿Sientes dolor en el pecho en reposo, en tu vida diaria o al realizar actividad física?' },
  { id: 'q3_dizziness', text: '3. ¿Pierdes el equilibrio por mareos o has perdido el conocimiento en los últimos 12 meses?' },
  { id: 'q4_chronic', text: '4. ¿Te han diagnosticado alguna otra condición médica crónica (diferente de cardiopatía o hipertensión)?' },
  { id: 'q5_medication', text: '5. ¿Tomas actualmente medicamentos recetados para una condición médica crónica?' },
  { id: 'q6_joint', text: '6. ¿Tienes actualmente (o has tenido en los últimos 12 meses) un problema óseo, articular o de tejidos blandos que pueda empeorar con el ejercicio?' },
  { id: 'q7_supervision', text: '7. ¿Alguna vez un médico te ha indicado que solo debes realizar actividad física bajo supervisión médica?' }
];

const MEDICAL_CONDITION_OPTIONS = [
  { id: 'pregnancy', label: 'Embarazo o postparto reciente' },
  { id: 'cardiopathy', label: 'Cardiopatía o arritmia conocida' },
  { id: 'hypertension', label: 'Hipertensión arterial no controlada' },
  { id: 'diabetes', label: 'Diabetes tipo 1 o 2' },
  { id: 'active_injury', label: 'Lesión osteoarticular o muscular activa' }
];

export default function OnboardingWizard({ onLogout, userId, userProfile, connectionParam, onComplete, isWithingsAuthenticated }) {
  const [step, setStep] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const conn = params.get('connection') || connectionParam;
    if (conn === 'strava_success') return 4;
    if (conn === 'withings_success') return 4;
    return 1;
  });

  useEffect(() => {
    if (connectionParam === 'strava_success') setStep(4);
    if (connectionParam === 'withings_success') setStep(4);
  }, [connectionParam]);

  const [loading, setLoading] = useState(false);
  const [athleteLevel, setAthleteLevel] = useState(userProfile?.preferences?.athleteLevel || 'intermedio');
  const [parqAnswers, setParqAnswers] = useState({
    q1_heart: false,
    q2_chest_pain: false,
    q3_dizziness: false,
    q4_chronic: false,
    q5_medication: false,
    q6_joint: false,
    q7_supervision: false
  });
  const [medicalConditions, setMedicalConditions] = useState([]);
  const [medicalClearanceConfirmed, setMedicalClearanceConfirmed] = useState(false);

  const hasAnyParqRisk = Object.values(parqAnswers).some(Boolean) || medicalConditions.length > 0;

  const [appleHealthActive, setAppleHealthActive] = useState(userProfile?.preferences?.appleHealthConnected || false);

  const handleStravaConnect = () => {
    connectProvider('strava', userId, API_URL);
  };

  const handleWithingsConnect = () => {
    if (isWithingsAuthenticated) return;
    connectProvider('withings', userId, API_URL);
  };

  const handleSaveParqAndAdvance = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/auth/parq`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parqAnswers,
          medicalConditions,
          medicalClearanceConfirmed
        })
      });
      await fetch(`${API_URL}/user/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ athleteLevel })
      }).catch(() => {});
      setStep(3);
    } catch (err) {
      console.error('Error guardando PAR-Q+:', err);
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAppleHealth = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const newStatus = !appleHealthActive;
      const response = await fetch(`${API_URL}/user/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ appleHealthConnected: newStatus })
      });
      if (response.ok) {
        setAppleHealthActive(newStatus);
      }
    } catch (error) {
      console.error('Error toggling Apple Health', error);
    } finally {
      setLoading(false);
    }
  };

  const [googleHealthActive, setGoogleHealthActive] = useState(userProfile?.preferences?.googleHealthConnected || false);
  const handleToggleGoogleHealth = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const newStatus = !googleHealthActive;
      const response = await fetch(`${API_URL}/user/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ googleHealthConnected: newStatus })
      });
      if (response.ok) {
        setGoogleHealthActive(newStatus);
      }
    } catch (error) {
      console.error('Error toggling Google Health', error);
    } finally {
      setLoading(false);
    }
  };

  const finishOnboarding = () => {
    setStep(5);
    setTimeout(() => onComplete && onComplete(), 1200);
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
      minHeight: '100vh', padding: '2rem', textAlign: 'center',
      background: 'radial-gradient(circle at 50% -20%, rgba(139, 92, 246, 0.15), transparent 60%)'
    }}>
      <div className="glass-panel stagger-1" style={{
        maxWidth: '680px', width: '100%', padding: '2.5rem',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        position: 'relative', overflow: 'hidden'
      }}>
        
        {/* Step Indicator */}
        <nav aria-label="Progreso de configuración" style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '2rem' }}>
          {[1, 2, 3, 4].map(i => (
            <div
              key={i}
              aria-current={i === step ? 'step' : undefined}
              style={{
                width: i === step ? '24px' : '8px',
                height: '8px',
                borderRadius: '4px',
                background: i === step ? 'linear-gradient(135deg, #00e5ff, #8b5cf6)' : (i < step ? '#10b981' : 'rgba(255,255,255,0.1)'),
                transition: 'all 0.3s ease'
              }}
            />
          ))}
        </nav>

        {/* STEP 1: WELCOME & EXPERIENCE LEVEL (M-02) */}
        {step === 1 && (
          <div style={{ animation: 'fadeIn 0.5s ease', width: '100%' }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(0, 229, 255, 0.2))',
              padding: '1.25rem', borderRadius: '50%', marginBottom: '1.25rem',
              display: 'inline-flex', boxShadow: '0 0 30px rgba(139, 92, 246, 0.2)'
            }}>
              <Sparkles size={38} style={{ color: '#00e5ff' }} aria-hidden="true" />
            </div>
            
            <h1 className="text-3xl text-gradient-health" style={{ marginBottom: '0.75rem' }}>
              Bienvenido a NousMetrics, {userProfile?.name?.split(' ')[0] || ''}
            </h1>
            <p className="text-muted" style={{ marginBottom: '1.5rem', lineHeight: '1.55', fontSize: '1rem' }}>
              Configura tu perfil deportivo y de seguridad en menos de 2 minutos para recibir tu primer análisis de predisposición diaria (Readiness).
            </p>

            <fieldset style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '1rem', marginBottom: '1.75rem', textAlign: 'left' }}>
              <legend style={{ padding: '0 0.5rem', fontSize: '0.85rem', fontWeight: 700, color: '#00e5ff' }}>
                Tu nivel de experiencia deportiva
              </legend>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem' }}>
                {[
                  { id: 'principiante', label: 'Principiante', desc: 'Rampa progresiva suave' },
                  { id: 'intermedio', label: 'Intermedio', desc: 'Entreno 3-5 días/sem' },
                  { id: 'avanzado', label: 'Avanzado', desc: 'Competición / Alto volumen' }
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    aria-pressed={athleteLevel === opt.id}
                    onClick={() => setAthleteLevel(opt.id)}
                    style={{
                      padding: '0.75rem',
                      borderRadius: '10px',
                      border: athleteLevel === opt.id ? '1.5px solid #00e5ff' : '1px solid rgba(255,255,255,0.1)',
                      background: athleteLevel === opt.id ? 'rgba(0, 229, 255, 0.12)' : 'rgba(255,255,255,0.03)',
                      color: 'white',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>{opt.label}</div>
                    <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)', marginTop: '2px' }}>{opt.desc}</div>
                  </button>
                ))}
              </div>
            </fieldset>
            
            <button 
              onClick={() => setStep(2)}
              style={{
                background: 'linear-gradient(135deg, #00e5ff, #8b5cf6)', color: 'white', border: 'none',
                padding: '1rem 2.5rem', borderRadius: '12px', fontSize: '1.05rem', fontWeight: 700,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0 auto',
                boxShadow: '0 8px 25px rgba(139, 92, 246, 0.4)', transition: 'transform 0.2s', justifyContent: 'center'
              }}
            >
              Continuar al Cribado de Seguridad <ArrowRight size={20} aria-hidden="true" />
            </button>
          </div>
        )}

        {/* STEP 2: CRIBADO PREVENTIVO PAR-Q+ Y CONDICIONES DE SALUD (B-05) */}
        {step === 2 && (
          <div style={{ animation: 'fadeIn 0.5s ease', width: '100%', textAlign: 'left' }}>
            <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
              <h1 className="text-2xl text-gradient-health" style={{ marginBottom: '0.4rem' }}>
                Cuestionario Preventivo de Salud (PAR-Q+)
              </h1>
              <p className="text-muted" style={{ fontSize: '0.88rem', margin: 0 }}>
                Por tu seguridad cardiovascular y articular, responde a estas 7 preguntas oficiales antes de recibir recomendaciones de intensidad.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', maxHeight: '320px', overflowY: 'auto', paddingRight: '0.25rem', marginBottom: '1rem' }}>
              {PARQ_QUESTIONS.map(q => (
                <div
                  key={q.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.65rem 0.85rem',
                    borderRadius: '10px',
                    background: parqAnswers[q.id] ? 'rgba(245, 158, 11, 0.12)' : 'rgba(255,255,255,0.03)',
                    border: parqAnswers[q.id] ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid rgba(255,255,255,0.07)'
                  }}
                >
                  <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.9)', lineHeight: 1.35, flex: 1 }}>
                    {q.text}
                  </span>
                  <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                    <button
                      type="button"
                      aria-pressed={!parqAnswers[q.id]}
                      onClick={() => setParqAnswers(prev => ({ ...prev, [q.id]: false }))}
                      style={{
                        padding: '0.35rem 0.7rem',
                        borderRadius: '6px',
                        border: !parqAnswers[q.id] ? '1px solid #10b981' : '1px solid rgba(255,255,255,0.15)',
                        background: !parqAnswers[q.id] ? 'rgba(16, 185, 129, 0.2)' : 'transparent',
                        color: !parqAnswers[q.id] ? '#34d399' : 'rgba(255,255,255,0.6)',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        cursor: 'pointer'
                      }}
                    >
                      No
                    </button>
                    <button
                      type="button"
                      aria-pressed={parqAnswers[q.id]}
                      onClick={() => setParqAnswers(prev => ({ ...prev, [q.id]: true }))}
                      style={{
                        padding: '0.35rem 0.7rem',
                        borderRadius: '6px',
                        border: parqAnswers[q.id] ? '1px solid #f59e0b' : '1px solid rgba(255,255,255,0.15)',
                        background: parqAnswers[q.id] ? 'rgba(245, 158, 11, 0.25)' : 'transparent',
                        color: parqAnswers[q.id] ? '#fbbf24' : 'rgba(255,255,255,0.6)',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        cursor: 'pointer'
                      }}
                    >
                      Sí
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <fieldset style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '0.75rem', marginBottom: '1rem' }}>
              <legend style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(255,255,255,0.75)', padding: '0 0.4rem' }}>
                Condiciones adicionales (marca si aplica alguna)
              </legend>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {MEDICAL_CONDITION_OPTIONS.map(cond => {
                  const checked = medicalConditions.includes(cond.id);
                  return (
                    <label
                      key={cond.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        padding: '0.35rem 0.65rem',
                        borderRadius: '8px',
                        fontSize: '0.76rem',
                        background: checked ? 'rgba(245, 158, 11, 0.15)' : 'rgba(255,255,255,0.03)',
                        border: checked ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid rgba(255,255,255,0.08)',
                        color: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMedicalConditions(prev => [...prev, cond.id]);
                          } else {
                            setMedicalConditions(prev => prev.filter(id => id !== cond.id));
                          }
                        }}
                      />
                      <span>{cond.label}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            {hasAnyParqRisk && (
              <div role="alert" style={{
                background: 'rgba(245, 158, 11, 0.12)',
                border: '1px solid rgba(245, 158, 11, 0.4)',
                borderRadius: '10px',
                padding: '0.85rem 1rem',
                marginBottom: '1rem',
                fontSize: '0.8rem',
                color: '#fde68a',
                lineHeight: 1.45
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700, marginBottom: '0.35rem', color: '#fbbf24' }}>
                  <ShieldAlert size={18} aria-hidden="true" /> Protección Preventiva Activada (Tope en MODERADO)
                </div>
                <p style={{ margin: '0 0 0.5rem 0' }}>
                  Has indicado algún factor de precaución en el cuestionario PAR-Q+. Te recomendamos <strong>consultar a tu médico</strong> antes de realizar esfuerzos intensos. Por seguridad, el motor bloqueará las sesiones de alta intensidad (`INTENSO` / Z4-Z5) y fijará tu techo en `MODERADO` (Z1-Z2).
                </p>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'white', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={medicalClearanceConfirmed}
                    onChange={e => setMedicalClearanceConfirmed(e.target.checked)}
                  />
                  <span>Confirmo que ya cuento con el visto bueno de un médico para realizar entrenamiento deportivo.</span>
                </label>
              </div>
            )}

            <button
              type="button"
              onClick={handleSaveParqAndAdvance}
              disabled={loading}
              style={{
                background: 'linear-gradient(135deg, #00e5ff, #8b5cf6)', color: 'white', border: 'none',
                padding: '0.95rem 2rem', borderRadius: '12px', fontSize: '1rem', fontWeight: 700,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%',
                justifyContent: 'center'
              }}
            >
              {loading ? 'Guardando cribado...' : 'Guardar y Continuar'} <ArrowRight size={18} aria-hidden="true" />
            </button>
          </div>
        )}

        {/* STEP 3: STRAVA */}
        {step === 3 && (
          <div style={{ animation: 'fadeIn 0.5s ease', width: '100%' }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(252, 76, 2, 0.2), rgba(252, 76, 2, 0.05))',
              padding: '1.25rem', borderRadius: '50%', marginBottom: '1.5rem',
              display: 'inline-flex', boxShadow: '0 0 30px rgba(252, 76, 2, 0.2)'
            }}>
              <Activity size={40} style={{ color: '#fc4c02' }} aria-hidden="true" />
            </div>
            
            <h1 className="text-3xl text-gradient-strava" style={{ marginBottom: '1rem' }}>
              Tu Hub de Entrenamiento
            </h1>
            <p className="text-muted" style={{ marginBottom: '2rem', lineHeight: '1.6' }}>
              Conecta Strava para sincronizar tu volumen de entrenamiento (GPS, potencia y frecuencia cardíaca) o continúa para introducir datos manualmente.
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
              {userProfile?.stravaConnected ? (
                <>
                  <div style={{ padding: '1rem', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', borderRadius: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', justifyContent: 'center' }}>
                    <CheckCircle size={20} aria-hidden="true" /> Strava Conectado
                  </div>
                  <button 
                    onClick={() => setStep(4)}
                    style={{ background: 'white', color: 'black', border: 'none', padding: '1rem 2.5rem', borderRadius: '12px', fontSize: '1.1rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0 auto', width: '100%', justifyContent: 'center' }}
                  >
                    Siguiente <ArrowRight size={20} aria-hidden="true" />
                  </button>
                </>
              ) : (
                <>
                  <button 
                    onClick={handleStravaConnect}
                    style={{
                      background: '#fc4c02', color: 'white', border: 'none',
                      padding: '1rem 2.5rem', borderRadius: '12px', fontSize: '1.1rem', fontWeight: 600,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0 auto',
                      boxShadow: '0 8px 25px rgba(252, 76, 2, 0.4)', transition: 'transform 0.2s', width: '100%', justifyContent: 'center'
                    }}
                  >
                    Conectar con Strava <ArrowRight size={20} aria-hidden="true" />
                  </button>
                  <button 
                    onClick={() => setStep(4)}
                    style={{ background: 'transparent', color: 'var(--text-secondary)', border: 'none', padding: '0.6rem', fontSize: '0.9rem', cursor: 'pointer' }}
                  >
                    Continuar sin Strava por ahora
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* STEP 4: HEALTH HUB & DIRECT ACCESS TO FIRST READINESS (M-02) */}
        {step === 4 && (
          <div style={{ animation: 'fadeIn 0.5s ease', width: '100%' }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(255, 45, 85, 0.2), rgba(0, 229, 255, 0.05))',
              padding: '1.25rem', borderRadius: '50%', marginBottom: '1.25rem',
              display: 'inline-flex', boxShadow: '0 0 30px rgba(255, 45, 85, 0.2)'
            }}>
              <HeartPulse size={40} style={{ color: '#ff2d55' }} aria-hidden="true" />
            </div>
            
            <h1 className="text-3xl text-gradient-health" style={{ marginBottom: '0.75rem' }}>
              Tu Hub de Salud y Biometría
            </h1>
            <p className="text-muted" style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
              Conecta tus sensores diarios de sueño, VFC (HRV) y frecuencia cardíaca en reposo para calcular tu primer índice de predisposición hoy mismo.
            </p>
            
            <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', borderLeft: '4px solid #ff2d55', fontSize: '0.84rem', color: 'rgba(255,255,255,0.85)', textAlign: 'left' }}>
              <strong>Privacidad garantizada (Art. 9 RGPD):</strong> Los datos leídos de Salud (Sueño, FC en reposo, VFC/HRV, SpO2, Peso y VO2max) se usan exclusivamente para calcular tu predisposición y recuperación diaria. No vendemos ni cedemos tus datos a terceros, y puedes retirar tu consentimiento en cualquier momento desde Ajustes.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '1rem', marginBottom: '1.75rem', textAlign: 'left' }}>
              <div style={{ background: 'rgba(255, 45, 85, 0.05)', border: '1px solid #ff2d55', borderRadius: '12px', padding: '1.2rem', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Smartphone size={18} color="#ff2d55" aria-hidden="true" /> <strong style={{ color: 'white', fontSize: '0.9rem' }}>Apple Health</strong>
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '1rem', flex: 1, lineHeight: '1.4' }}>Recomendado en iOS. Sincroniza Apple Watch y básculas compatibles.</p>
                <button 
                  onClick={handleToggleAppleHealth} disabled={loading}
                  style={{ background: appleHealthActive ? 'rgba(16, 185, 129, 0.2)' : '#ff2d55', color: appleHealthActive ? '#10b981' : 'white', border: 'none', padding: '0.6rem', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                >
                  {appleHealthActive ? <><CheckCircle size={16} aria-hidden="true" /> Activado</> : 'Activar'}
                </button>
              </div>

              <div style={{ background: 'rgba(66, 133, 244, 0.05)', border: '1px solid #4285F4', borderRadius: '12px', padding: '1.2rem', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Smartphone size={18} color="#4285F4" aria-hidden="true" /> <strong style={{ color: 'white', fontSize: '0.9rem' }}>Health Connect</strong>
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '1rem', flex: 1, lineHeight: '1.4' }}>Recomendado en Android. Conecta Garmin, Samsung Health o Fitbit vía Health Connect.</p>
                <button 
                  onClick={handleToggleGoogleHealth} disabled={loading}
                  style={{ background: googleHealthActive ? 'rgba(16, 185, 129, 0.2)' : '#4285F4', color: googleHealthActive ? '#10b981' : 'white', border: 'none', padding: '0.6rem', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                >
                  {googleHealthActive ? <><CheckCircle size={16} aria-hidden="true" /> Activado</> : 'Activar'}
                </button>
              </div>

              <div style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid var(--glass-border)', borderRadius: '12px', padding: '1.2rem', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Activity size={18} color="#00e5ff" aria-hidden="true" /> <strong style={{ color: 'white', fontSize: '0.9rem' }}>Withings</strong>
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '1rem', flex: 1, lineHeight: '1.4' }}>Sincronización directa en la nube para básculas y sensores de sueño Withings.</p>
                <button 
                  onClick={handleWithingsConnect}
                  disabled={isWithingsAuthenticated}
                  style={{ background: isWithingsAuthenticated ? 'rgba(16, 185, 129, 0.2)' : 'transparent', color: isWithingsAuthenticated ? '#10b981' : '#00e5ff', border: isWithingsAuthenticated ? 'none' : '1px solid #00e5ff', padding: '0.6rem', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 'bold', cursor: isWithingsAuthenticated ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                >
                  {isWithingsAuthenticated ? <><CheckCircle size={16} aria-hidden="true" /> Activado</> : 'Conectar Withings'}
                </button>
              </div>
            </div>

            <button 
              onClick={finishOnboarding}
              disabled={loading}
              style={{
                background: 'linear-gradient(135deg, #00e5ff, #8b5cf6)',
                color: 'white', border: 'none', padding: '1rem 2.5rem',
                borderRadius: '12px', fontSize: '1.05rem', fontWeight: 700, width: '100%',
                cursor: loading ? 'wait' : 'pointer', transition: 'all 0.2s',
                boxShadow: '0 8px 25px rgba(139, 92, 246, 0.4)', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem'
              }}
            >
              Ver mi Predisposición Diaria (Readiness) <ArrowRight size={20} aria-hidden="true" />
            </button>
          </div>
        )}

        {/* STEP 5: SUCCESS */}
        {step === 5 && (
          <div style={{ animation: 'fadeIn 0.5s ease', width: '100%' }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(16, 185, 129, 0.05))',
              padding: '1.25rem', borderRadius: '50%', marginBottom: '1.5rem',
              display: 'inline-flex', boxShadow: '0 0 30px rgba(16, 185, 129, 0.2)'
            }}>
              <CheckCircle size={40} style={{ color: '#10b981' }} />
            </div>
            
            <h1 className="text-3xl text-gradient" style={{ marginBottom: '1rem' }}>
              ¡Todo listo!
            </h1>
            <p className="text-muted" style={{ marginBottom: '2.5rem', lineHeight: '1.6' }}>
              Hemos configurado tu perfil. Prepárate para descubrir una nueva forma de entrenar.
            </p>
            
            <div style={{ width: '40px', height: '40px', border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#00e5ff', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
          </div>
        )}

      </div>
      
      {step < 5 && (
        <button 
          onClick={onLogout} 
          style={{ 
            marginTop: '2rem', background: 'transparent', color: 'var(--text-muted)', 
            border: 'none', cursor: 'pointer', fontSize: '0.9rem',
            transition: 'color 0.2s'
          }}
        >
          Cancelar y cerrar sesión
        </button>
      )}
    </div>
  );
}
